import React, { useState } from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText,
  Paper,
  Typography 
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Update topics to include premium flag
export const topics = [
  { id: 'binary-search', name: 'Binary Search', count: '', premium: false },
  { id: 'arrays-and-hashing', name: 'Arrays & Hashing', count: '', premium: true},
  { id: 'two-pointer', name: 'Two Pointers', count: '', premium: true },
  { id: 'sliding-window', name: 'Sliding Window', count: '', premium: true, subtopics: [
    // { id: 'fixed', name: 'Fixed Sliding Window' , premium: true},
    // { id: 'dynamic', name: 'Dynamic Sliding Window', premium: true}
  ] },
  { id: 'parenthesis', name: 'Parenthesis', count: '', premium: true},
  { id: 'linked-list', name: 'Linked List', count: '', premium: true },
  { id: 'tree', name: 'Trees', count: '', premium: true },
  { id: 'trie', name: 'Trie', count: '', premium: true },
  { id: 'heap', name: 'Heap', count: '', premium: true },
  { id: 'backtracking', name: 'Backtracking', count: '', premium: true },
  { id: 'graph', name: 'Graphs', count: '', premium: true },
  { id: 'dynamic-programming', name: 'Dynamic Programming', count: '', premium: true },
  { id: 'greedy', name: 'Greedy', count: '', premium: true },
  { id: 'interval', name: 'Intervals', count: '', premium: true },
  { id: 'monotonic-stack', name: 'Monotonic Stack', count: '', premium: true },
  { id: 'palindrome', name: 'Palindromes', count: '', premium: true },
  { id: 'divide-and-conquer', name: 'Divide and Conquer', count: '', premium: true },
  { id: 'prefix-sum', name: 'Prefix Sum', count: '', premium: true },
  { id: 'sorted-list', name: 'Sorted List', count: '', premium: true },
  { id: 'segment-tree', name: 'Segment Tree', count: '', premium: true },
  { id: 'coming-soon', name: 'Coming Soon', count: '', premium: true },
];

// Helper to check if all sections/problems under a topic are completed
function isTopicFullyCompleted(topicId, mode, completedChapters, quizzesByTopic, flashcardsByTopic) {
  if (!completedChapters) return false;
  try {
    if (mode === 'quizzes') {
      if (!quizzesByTopic || !quizzesByTopic[topicId]) return false;
      const problems = quizzesByTopic[topicId].problems;
      return problems.every(problem => completedChapters[`${topicId}-${problem.id}`]);
    } else if (mode === 'flashcards') {
      if (!flashcardsByTopic || !flashcardsByTopic[topicId]) return false;
      const problems = flashcardsByTopic[topicId].problems;
      return problems.every(problem => completedChapters[`${topicId}-${problem.id}`]);
    } else if (mode === 'courses') {
      // For courses, completedChapters[topicId] is enough
      return !!completedChapters[topicId];
    }
  } catch {
    return false;
  }
  return false;
}

function Sidebar({ mode, onTopicSelect, selectedTopic, completedChapters, quizzesByTopic, flashcardsByTopic }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split('/')[2] || '';
  const { hasActiveSubscription, loading } = useSubscriptionStatus();
  const [expandedTopics, setExpandedTopics] = useState({});

  // either a topic object, or a string of the form 'parent:sub'
  const handleTopicClick = (topicOrId) => {
    if (typeof topicOrId === 'string') {
      // subtopic click (id is 'parent:sub')
      const [parentId] = topicOrId.split(':');
      const parentTopic = topics.find(t => t.id === parentId);
      if (parentTopic && parentTopic.premium && !hasActiveSubscription) {
        navigate('/purchase');
        return;
      }
      onTopicSelect(topicOrId);
      return;
    }
    const topic = topicOrId;
    if (topic.premium && !hasActiveSubscription) {
      navigate('/purchase');
    } else {
      onTopicSelect(topic.id);
    }
  };

  const handleExpandToggle = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  if (loading) {
    return <Paper sx={{ p: 2 }}>Loading...</Paper>;
  }

  return (
    <Paper
      sx={{
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'text.primary',
            fontWeight: 850,
          }}
        >
          Topics
        </Typography>
      </Box>
      <List sx={{ p: 0 }}>
        {topics.map((topic) => (
          <React.Fragment key={topic.id}>
            <ListItem disablePadding>
              <ListItemButton 
                selected={mode === 'problems' ? currentPath === topic.id : selectedTopic === topic.id}
                onClick={() => handleTopicClick(topic)}
                disabled={topic.id === 'coming-soon'}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'rgba(81, 104, 255, 0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(81, 104, 255, 0.11)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  ...(topic.id === 'coming-soon' && {
                    color: 'text.disabled',
                    cursor: 'not-allowed',
                    opacity: 0.7,
                    pointerEvents: 'none',
                  })
                }}
              >
                <ListItemText 
                  primary={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {topic.name}
                      {((mode === 'courses' && completedChapters && completedChapters[topic.id]) ||
                        ((mode === 'quizzes' || mode === 'flashcards') && isTopicFullyCompleted(topic.id, mode, completedChapters, quizzesByTopic, flashcardsByTopic))) && (
                        <CheckCircleIcon sx={{ color: '#15905f', fontSize: 18, ml: 0.5, verticalAlign: 'middle' }} />
                      )}
                    </span>
                  }
                  secondary={topic.count}
                  primaryTypographyProps={{ 
                    sx: { 
                      color: topic.id === 'coming-soon' ? 'text.disabled' : (topic.premium && !hasActiveSubscription ? 'text.secondary' : 'text.primary'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                  secondaryTypographyProps={{ 
                    sx: { color: 'text.secondary' }
                  }}
                />
                {topic.premium && !hasActiveSubscription && topic.id !== 'coming-soon' && (
                  <LockIcon 
                    sx={{ 
                      color: 'secondary.main',
                      ml: 1,
                      fontSize: '1.2rem'
                    }} 
                  />
                )}
                {/* Expand/collapse icon for subtopics */}
                {topic.subtopics && topic.subtopics.length > 0 && topic.id !== 'coming-soon' && (
                  <Box sx={{ ml: 1 }}>
                    <span onClick={e => { e.stopPropagation(); handleExpandToggle(topic.id); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      {expandedTopics[topic.id] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </span>
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
            {/* Render subtopics if present and expanded */}
            {topic.subtopics && topic.subtopics.length > 0 && topic.id !== 'coming-soon' && expandedTopics[topic.id] && (
              <List disablePadding sx={{ pl: 4 }}>
                {topic.subtopics.map((sub) => (
                  <ListItem key={sub.id} disablePadding>
                    <ListItemButton
                      selected={selectedTopic === `${topic.id}:${sub.id}`}
                      onClick={() => handleTopicClick(`${topic.id}:${sub.id}`)}
                      sx={{
                        pl: 2,
                        color: topic.id === 'coming-soon' ? 'text.disabled' : (topic.premium && !hasActiveSubscription ? 'text.secondary' : 'text.primary'),
                        '&.Mui-selected': {
                          bgcolor: 'rgba(97, 218, 251, 0.08)',
                          color: 'secondary.main',
                        },
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText primary={sub.name} />
                      {topic.premium && !hasActiveSubscription && topic.id !== 'coming-soon' && (
                        <LockIcon 
                          sx={{ 
                            color: 'secondary.main',
                            ml: 1,
                            fontSize: '1.2rem'
                          }} 
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
}

export default Sidebar; 
